// ═══════════════════════════════════════════════════════════════════════════════
// Shared button — primary / secondary / ghost / danger (see Doc/frontend_ui_style_guide.md)
// ═══════════════════════════════════════════════════════════════════════════════

import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';

export type AppButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type AppButtonSize = 'md' | 'lg' | 'pill';

type AppButtonClassNameOptions = {
  variant?: AppButtonVariant;
  size?: AppButtonSize;
  fullWidth?: boolean;
  /** Layout / spacing only — do not override colors. */
  className?: string;
};

type AppButtonProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> &
  AppButtonClassNameOptions & {
    children: ReactNode;
  };

const VARIANT_CLASS: Record<AppButtonVariant, string> = {
  primary:
    'bg-primary-600 text-white hover:bg-primary-700 focus-visible:ring-primary-500 dark:bg-primary-700 dark:hover:bg-primary-600',
  secondary:
    'border border-gray-300 bg-white text-gray-800 hover:bg-gray-50 focus-visible:ring-primary-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100 dark:hover:bg-gray-800',
  ghost:
    'text-primary-700 hover:bg-primary-50 focus-visible:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-950/40',
  danger:
    'bg-red-600 text-white hover:bg-red-700 focus-visible:ring-red-500 dark:bg-red-700 dark:hover:bg-red-600',
};

const SIZE_CLASS: Record<AppButtonSize, string> = {
  md: 'min-h-11 rounded-xl px-4 py-2 text-sm font-semibold',
  lg: 'min-h-12 rounded-xl px-4 py-3 text-base font-bold',
  pill: 'min-h-11 rounded-full px-4 py-3 text-base font-semibold',
};

const RADIUS_OVERRIDE: Partial<Record<AppButtonVariant, Partial<Record<AppButtonSize, string>>>> = {
  secondary: { md: 'rounded-lg', lg: 'rounded-lg' },
  ghost: { md: 'rounded-lg', lg: 'rounded-lg' },
};

/** Shared class string for `<button>` and `<Link>` so visual styles stay aligned. */
export const appButtonClassName = ({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  className = '',
}: AppButtonClassNameOptions = {}): string => {
  const radiusOverride = RADIUS_OVERRIDE[variant]?.[size];
  const sizeClass = radiusOverride
    ? `${SIZE_CLASS[size].replace(/rounded-\w+/, radiusOverride)}`
    : SIZE_CLASS[size];

  return [
    'inline-flex items-center justify-center gap-2 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus-visible:ring-offset-gray-900',
    sizeClass,
    VARIANT_CLASS[variant],
    fullWidth ? 'w-full' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');
};

export const AppButton = forwardRef<HTMLButtonElement, AppButtonProps>(
  (
    {
      variant = 'primary',
      size = 'md',
      fullWidth = false,
      type = 'button',
      disabled,
      className = '',
      children,
      ...rest
    },
    ref,
  ) => (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      className={appButtonClassName({ variant, size, fullWidth, className })}
      {...rest}
    >
      {children}
    </button>
  ),
);

AppButton.displayName = 'AppButton';
